/**
 * /terceirizacao/oses/:id — Detalhe da OSE.
 *
 * Layout em 3 zonas:
 *   Zona 1: EntityHeader (numero + parceiro + situacao badge) + actions
 *           contextuais (enviar/recebimento/retorno/aprovar/rejeitar/
 *           cancelar/comentar) dependendo da situacao
 *   Zona 2: KpiStrip (categoria, qtd ops, prazo, status atraso)
 *   Zona 3: Grid (info da OSE + lista de OPs + timeline de eventos)
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FiArrowLeft,
  FiCheckCircle,
  FiClock,
  FiDollarSign,
  FiMessageCircle,
  FiRefreshCw,
  FiSend,
  FiTrash2,
  FiXCircle,
} from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import EntityHeader from "../../../components/EntityHeader";
import KpiStrip from "../../../components/KpiStrip";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import {
  aprovarOse,
  atualizarOse,
  cancelarOse,
  comentarOse,
  enviarOse,
  obterOse,
  registrarRecebimento,
  registrarRetorno,
  rejeitarOse,
  SITUACAO_LABELS,
  SITUACAO_TONES,
} from "../../../services/terceirizacao";
import { PageContainer } from "../../../styles/shared";

import { fmtData, fmtMoeda } from "../../Orcamentos/helpers";
import {
  Card,
  EmptyMsg,
  ErrorMsg,
  Input,
  Pill,
  SectionTitle,
  Table,
  TableWrapper,
  Textarea,
} from "../../Orcamentos/styles";


// Cor por tipo de evento na timeline
const EVENTO_TONES = {
  ENVIO: "info",
  RECEBIMENTO_TERCEIRO: "info",
  RETORNO: "warning",
  APROVACAO_CQ: "success",
  REJEICAO_CQ: "danger",
  CANCELAMENTO: "neutral",
  COMENTARIO: "neutral",
};


export default function OseDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { request, hasAllPermissions, isMaster } = useAuth();

  const [ose, setOse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmando, setConfirmando] = useState(null);
  const [motivoOuObs, setMotivoOuObs] = useState("");
  const [comentando, setComentando] = useState(false);
  const [novoComentario, setNovoComentario] = useState("");
  const [editandoValor, setEditandoValor] = useState(false);
  const [novoValor, setNovoValor] = useState("");

  const podeEnviar = isMaster || hasAllPermissions(["terceirizacao:envio"]);
  const podeRetorno = isMaster || hasAllPermissions(["terceirizacao:retorno"]);
  const podeCq = isMaster || hasAllPermissions(["terceirizacao:approve_qc"]);
  const podeCriar = isMaster || hasAllPermissions(["terceirizacao:create_ose"]);

  const carregar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await obterOse(request, id);
      setOse(data);
    } catch (err) {
      setError(err?.message || "Erro ao carregar OSE.");
      setOse(null);
    } finally {
      setLoading(false);
    }
  }, [request, id]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Actions disponiveis por situacao
  const actions = (() => {
    if (!ose) return [];
    const out = [];
    out.push({
      label: "Atualizar",
      icon: <FiRefreshCw size={14} />,
      onClick: carregar,
    });
    if (ose.situacao === "RASCUNHO" && podeEnviar) {
      out.push({
        label: "Enviar ao terceiro",
        icon: <FiSend size={14} />,
        onClick: () => setConfirmando("enviar"),
        primary: true,
      });
    }
    if (ose.situacao === "ENVIADA" && podeEnviar) {
      out.push({
        label: "Confirmar recebimento",
        icon: <FiCheckCircle size={14} />,
        onClick: () => setConfirmando("recebimento"),
      });
    }
    if (
      (ose.situacao === "ENVIADA" || ose.situacao === "EM_PRODUCAO_TERCEIRO")
      && podeRetorno
    ) {
      out.push({
        label: "Registrar retorno",
        icon: <FiClock size={14} />,
        onClick: () => setConfirmando("retorno"),
        primary: ose.situacao === "EM_PRODUCAO_TERCEIRO",
      });
    }
    if (ose.situacao === "RETORNADA" && podeCq) {
      out.push({
        label: "Aprovar (CQ)",
        icon: <FiCheckCircle size={14} />,
        onClick: () => setConfirmando("aprovar"),
        primary: true,
      });
      out.push({
        label: "Rejeitar (CQ)",
        icon: <FiXCircle size={14} />,
        onClick: () => setConfirmando("rejeitar"),
      });
    }
    if (ose.pode_editar_valor && podeCriar) {
      out.push({
        label: ose.valor === null || ose.valor === undefined
          ? "Informar valor"
          : "Editar valor",
        icon: <FiDollarSign size={14} />,
        onClick: () => {
          setNovoValor(ose.valor === null || ose.valor === undefined ? "" : String(ose.valor));
          setEditandoValor(true);
        },
        // Sem valor, a aprovacao do CQ nao gera conta a pagar — puxa atencao.
        primary: ose.situacao === "RETORNADA"
          && (ose.valor === null || ose.valor === undefined),
      });
    }
    if (!ose.situacao || (!["APROVADA", "REJEITADA", "CANCELADA"].includes(ose.situacao) && podeCriar)) {
      out.push({
        label: "Cancelar",
        icon: <FiTrash2 size={14} />,
        onClick: () => setConfirmando("cancelar"),
      });
    }
    out.push({
      label: "Comentar",
      icon: <FiMessageCircle size={14} />,
      onClick: () => setComentando(true),
    });
    return out;
  })();

  const executarAcao = useCallback(async () => {
    if (!ose || !confirmando) return;
    let promise;
    if (confirmando === "enviar") {
      promise = enviarOse(request, id, { checklist: {} });
    } else if (confirmando === "recebimento") {
      promise = registrarRecebimento(request, id, { descricao: motivoOuObs });
    } else if (confirmando === "retorno") {
      promise = registrarRetorno(request, id, { checklist: {} });
    } else if (confirmando === "aprovar") {
      promise = aprovarOse(request, id, { observacao: motivoOuObs });
    } else if (confirmando === "rejeitar") {
      if (!motivoOuObs.trim()) {
        throw new Error("Motivo da rejeição é obrigatório.");
      }
      promise = rejeitarOse(request, id, { motivo: motivoOuObs });
    } else if (confirmando === "cancelar") {
      if (!motivoOuObs.trim()) {
        throw new Error("Motivo do cancelamento é obrigatório.");
      }
      promise = cancelarOse(request, id, { motivo: motivoOuObs });
    } else {
      return;
    }
    const data = await promise;
    setOse(data);
    setConfirmando(null);
    setMotivoOuObs("");
  }, [confirmando, motivoOuObs, request, id, ose]);

  const salvarValor = useCallback(async () => {
    const limpo = novoValor.trim().replace(",", ".");
    if (limpo && (Number.isNaN(Number(limpo)) || Number(limpo) < 0)) {
      throw new Error("Valor do serviço inválido.");
    }
    const data = await atualizarOse(request, id, { valor: limpo || null });
    setOse(data);
    setEditandoValor(false);
    setNovoValor("");
  }, [novoValor, request, id]);

  const enviarComentario = useCallback(async () => {
    if (!novoComentario.trim()) {
      throw new Error("Comentário não pode estar vazio.");
    }
    const data = await comentarOse(request, id, { texto: novoComentario });
    setOse(data);
    setComentando(false);
    setNovoComentario("");
  }, [novoComentario, request, id]);

  if (loading && !ose) {
    return (
      <PageContainer>
        <EmptyMsg>Carregando OSE...</EmptyMsg>
      </PageContainer>
    );
  }

  if (error && !ose) {
    return (
      <PageContainer>
        <EntityHeader
          title="Erro"
          actions={[{
            label: "Voltar",
            icon: <FiArrowLeft size={14} />,
            onClick: () => navigate("/terceirizacao/oses"),
          }]}
        />
        <ErrorMsg>{error}</ErrorMsg>
      </PageContainer>
    );
  }

  if (!ose) return null;

  // null/undefined = ninguém informou (≠ zero, que seria cortesia).
  const temValor = ose.valor !== null && ose.valor !== undefined;

  const kpis = [
    {
      label: "Categoria",
      value: ose.categoria_display || ose.categoria,
      tone: "neutral",
    },
    {
      label: "Peças",
      value: ose.ops_detalhadas?.length || 0,
      tone: (ose.ops_detalhadas?.length || 0) > 0 ? "info" : "warning",
    },
    {
      label: "Prazo",
      value: ose.prazo_retorno_esperado ? fmtData(ose.prazo_retorno_esperado) : "—",
      tone: ose.atrasada ? "danger" : "neutral",
      sub: ose.atrasada ? `${ose.dias_em_atraso}d atraso` : "",
    },
    {
      label: "SLA",
      value: ose.parceiro_sla_dias ? `${ose.parceiro_sla_dias}d` : "—",
      tone: "neutral",
    },
    {
      label: "Valor do serviço",
      value: temValor ? fmtMoeda(ose.valor) : "Não informado",
      tone: temValor ? "info" : "warning",
      sub: temValor ? "" : "sem isso a margem do pedido sai inflada",
    },
  ];

  return (
    <PageContainer>
      <EntityHeader
        title={ose.numero}
        subtitle={`${ose.parceiro_nome} — ${ose.descricao_servico}`}
        badges={[
          { label: SITUACAO_LABELS[ose.situacao] || ose.situacao, tone: SITUACAO_TONES[ose.situacao] || "neutral" },
          ...(ose.atrasada ? [{ label: `Atrasada ${ose.dias_em_atraso}d`, tone: "danger" }] : []),
        ]}
        actions={[
          {
            label: "Voltar",
            icon: <FiArrowLeft size={14} />,
            onClick: () => navigate("/terceirizacao/oses"),
          },
          ...actions,
        ]}
      />

      <KpiStrip items={kpis} />

      {error && <ErrorMsg>{error}</ErrorMsg>}

      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
        gap: 16,
      }}>
        {/* Card: dados da OSE */}
        <Card>
          <SectionTitle>Dados da OSE</SectionTitle>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 14 }}>
            <div><strong>Número:</strong> {ose.numero}</div>
            <div><strong>Parceiro:</strong> {ose.parceiro_nome}</div>
            <div><strong>Categoria:</strong> {ose.categoria_display || ose.categoria}</div>
            <div><strong>Serviço:</strong> {ose.descricao_servico}</div>
            <div>
              <strong>Valor do serviço:</strong>{" "}
              {temValor ? fmtMoeda(ose.valor) : (
                <Pill $tone="warning">não informado</Pill>
              )}
            </div>
            {!temValor && !ose.eh_terminal && (
              <small style={{ opacity: 0.75 }}>
                Informe o valor antes de aprovar no CQ — é ele que vira a conta
                a pagar do parceiro no Financeiro.
              </small>
            )}
            {!temValor && ose.eh_terminal && (
              <small style={{ opacity: 0.75 }}>
                OSE finalizada sem valor: nenhuma conta a pagar foi gerada.
                Lance a despesa manualmente no Financeiro.
              </small>
            )}
            <hr style={{ border: "none", borderTop: "1px solid var(--border, #e5e7eb)", margin: "4px 0" }} />
            <div><strong>Criada em:</strong> {fmtData(ose.created_at)}</div>
            {ose.enviada_em && <div><strong>Enviada em:</strong> {fmtData(ose.enviada_em)}</div>}
            {ose.recebida_terceiro_em && <div><strong>Recebida em:</strong> {fmtData(ose.recebida_terceiro_em)}</div>}
            {ose.retornada_em && <div><strong>Retornada em:</strong> {fmtData(ose.retornada_em)}</div>}
            {ose.aprovada_em && <div><strong>Aprovada em:</strong> {fmtData(ose.aprovada_em)}</div>}
            {ose.rejeitada_em && (
              <>
                <div><strong>Rejeitada em:</strong> {fmtData(ose.rejeitada_em)}</div>
                <div><strong>Motivo:</strong> {ose.motivo_rejeicao}</div>
              </>
            )}
            {ose.cancelada_em && (
              <>
                <div><strong>Cancelada em:</strong> {fmtData(ose.cancelada_em)}</div>
                <div><strong>Motivo:</strong> {ose.motivo_cancelamento}</div>
              </>
            )}
          </div>
        </Card>

        {/* Card: OPs vinculadas */}
        <Card>
          <SectionTitle>OPs no lote ({ose.ops_detalhadas?.length || 0})</SectionTitle>
          {(!ose.ops_detalhadas || ose.ops_detalhadas.length === 0) ? (
            <EmptyMsg>Nenhuma OP vinculada.</EmptyMsg>
          ) : (
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <th>OP</th>
                    <th>Pedido</th>
                    <th>Descrição</th>
                    <th>Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Sem drill-down: o detalhe de OP avulso foi removido no
                      P5 — a peça vive no Detalhe do Ambiente da Produção v2
                      (o resumo da OSE não carrega o ambiente de produção). */}
                  {ose.ops_detalhadas.map((op) => (
                    <tr key={op.id}>
                      <td><strong>{op.numero}</strong></td>
                      <td>{op.pedido_numero}</td>
                      <td style={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          title={op.descricao}>
                        {op.descricao}
                      </td>
                      <td>{op.situacao}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>
          )}
        </Card>
      </div>

      {/* Timeline de eventos */}
      <Card>
        <SectionTitle>Histórico</SectionTitle>
        {(!ose.eventos || ose.eventos.length === 0) ? (
          <EmptyMsg>Nenhum evento ainda.</EmptyMsg>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {ose.eventos.map((ev) => (
              <div
                key={ev.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "160px 1fr",
                  gap: 12,
                  paddingBottom: 12,
                  borderBottom: "1px solid var(--border, #e5e7eb)",
                }}
              >
                <div style={{ fontSize: 12, opacity: 0.7 }}>
                  {fmtData(ev.timestamp)}
                </div>
                <div>
                  <Pill $tone={EVENTO_TONES[ev.tipo] || "neutral"}>{ev.tipo_display}</Pill>
                  {ev.descricao && (
                    <div style={{ marginTop: 4, fontSize: 14 }}>{ev.descricao}</div>
                  )}
                  <div style={{ fontSize: 12, opacity: 0.6, marginTop: 4 }}>
                    por {ev.ator_nome}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Modal de confirmacao de acao */}
      {confirmando && (
        <SlideOverPanel
          open
          onClose={() => { setConfirmando(null); setMotivoOuObs(""); }}
          title={({
            enviar: "Enviar OSE ao terceiro",
            recebimento: "Confirmar recebimento pelo terceiro",
            retorno: "Registrar retorno das peças",
            aprovar: "Aprovar OSE no CQ",
            rejeitar: "Rejeitar OSE no CQ",
            cancelar: "Cancelar OSE",
          })[confirmando]}
          footer={
            <AsyncButton
              variant={
                confirmando === "rejeitar" || confirmando === "cancelar"
                  ? "danger"
                  : "primary"
              }
              onClick={executarAcao}
              successMessage={`Ação ${confirmando} executada`}
            >
              Confirmar
            </AsyncButton>
          }
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {(confirmando === "rejeitar" || confirmando === "cancelar") && (
              <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>Motivo *</span>
                <Textarea
                  rows={3}
                  value={motivoOuObs}
                  onChange={(e) => setMotivoOuObs(e.target.value)}
                  placeholder="Obrigatório - explique o motivo"
                  maxLength={300}
                />
              </label>
            )}
            {(confirmando === "aprovar" || confirmando === "recebimento") && (
              <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>Observação (opcional)</span>
                <Textarea
                  rows={3}
                  value={motivoOuObs}
                  onChange={(e) => setMotivoOuObs(e.target.value)}
                  maxLength={300}
                />
              </label>
            )}
            <div style={{ fontSize: 13, opacity: 0.8 }}>
              {confirmando === "enviar" && "Marca OSE como ENVIADA + calcula prazo de retorno conforme SLA do parceiro."}
              {confirmando === "recebimento" && "Confirma que o terceiro recebeu as peças e já está trabalhando."}
              {confirmando === "retorno" && "Marca peças como retornadas; agora aguarda CQ aprovar/rejeitar."}
              {confirmando === "aprovar" && (
                temValor
                  ? `CQ aprovou - OPs são liberadas para próxima estação. Gera a conta a pagar de ${fmtMoeda(ose.valor)} ao parceiro. Terminal.`
                  : "CQ aprovou - OPs são liberadas para próxima estação. Terminal. Atenção: esta OSE não tem valor informado, então NENHUMA conta a pagar será gerada e o valor congela após a aprovação — informe o valor antes se o serviço foi cobrado."
              )}
              {confirmando === "rejeitar" && "CQ rejeitou - PCP precisa decidir próximo passo (nova OSE ou cancelamento). Terminal."}
              {confirmando === "cancelar" && "Aborta OSE permanentemente. Terminal."}
            </div>
          </div>
        </SlideOverPanel>
      )}

      {/* Modal de valor do servico */}
      {editandoValor && (
        <SlideOverPanel
          open
          onClose={() => { setEditandoValor(false); setNovoValor(""); }}
          title="Valor do serviço"
          subtitle={`${ose.numero} — ${ose.parceiro_nome}`}
          footer={
            <AsyncButton
              variant="primary"
              onClick={salvarValor}
              successMessage="Valor do serviço atualizado"
            >
              Salvar
            </AsyncButton>
          }
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 600 }}>Valor acordado (R$)</span>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={novoValor}
                onChange={(e) => setNovoValor(e.target.value)}
                placeholder="Ex: 1250.00"
              />
            </label>
            <div style={{ fontSize: 13, opacity: 0.8 }}>
              Quando o CQ aprovar a OSE, este valor vira automaticamente uma
              conta a pagar ao parceiro (Financeiro → Contas a Pagar). Depois
              da aprovação o valor congela.
            </div>
          </div>
        </SlideOverPanel>
      )}

      {/* Modal de comentario */}
      {comentando && (
        <SlideOverPanel
          open
          onClose={() => { setComentando(false); setNovoComentario(""); }}
          title="Adicionar comentário"
          subtitle="Comentário interno - não muda situação"
          footer={
            <AsyncButton
              variant="primary"
              onClick={enviarComentario}
              successMessage="Comentário adicionado"
            >
              Adicionar
            </AsyncButton>
          }
        >
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Texto *</span>
            <Textarea
              rows={4}
              value={novoComentario}
              onChange={(e) => setNovoComentario(e.target.value)}
              placeholder="Ex: Cliente pediu para urgenciar a laquação"
              maxLength={500}
            />
          </label>
        </SlideOverPanel>
      )}
    </PageContainer>
  );
}
